import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BuildIcon from "@mui/icons-material/Build";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import KeySharpIcon from "@mui/icons-material/KeySharp";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import { Box, type BoxProps, styled } from "@mui/material";
import { rem } from "polished";
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink, type NavLinkProps } from "react-router-dom";
import {
  ACCOUNTING_PAGE,
  ariaLabel,
  COUNCIL_PAGE,
  DASHBOARD_PAGE,
  DOCUMENTS_PAGE,
  ESTATE_PAGE,
  focusOutline,
  GENERAL_ASSEMBLY_PAGE,
  MISSION_INCIDENTS_PAGE,
  NOTIFICATIONS_PAGE,
  OFFERS_AND_SERVICES_PAGE,
  PATH_LANDLORD_REPAIR_FUNDS_CALLS,
  PAYMENTS_PAGE,
  RESTRICTED_DASHBOARD_PAGE,
  TENANTS_BALANCE_PAGE,
  TICKETS_PAGE,
} from "src/constants";
import {
  AccountQualityEnum,
  AnalyticsCustomEventNameEnum,
  FeatureFlagNamesEnum,
} from "src/graphql/generated/types";
import {
  useCurrentAccount,
  useHasFeatureFlagActivated,
  useMarketplaceLink,
} from "src/hooks";
import { UnreadCountBadge } from "src/pages/Notifications/_components/UnreadCountBadge";
import { logCustomEvent } from "src/utils";

export interface MainNavItemProps extends NavLinkProps {
  label: string;
  slug: string;
}

export type MainLayoutNavBarProps = {
  hasRestrictedAccess: boolean;
  hasAccessToGeneralAssembly: boolean;
  isTrusteeCouncilMember: boolean;
  accountQuality: AccountQualityEnum;
  closeSidebar(): void;
} & BoxProps;

const NavLinkComponent = (props: NavLinkProps) => <NavLink {...props} />;

const StyledItem = styled(NavLinkComponent)`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${rem(60)};
  background: ${(props) => props.theme.palette.common.white};
  border-left: ${rem(6)} solid ${(props) => props.theme.palette.secondary.main};
  text-decoration: none;
  padding: 0 ${(props) => props.theme.spacing(3)};

  color: ${(props) => props.theme.palette.secondary.main};
  font-size: ${rem(16)};
  font-weight: 500;

  &:not(:first-of-type) {
    margin-top: 1px;
  }

  &:focus-visible: ${focusOutline}

  svg {
    margin-right: ${(props) => props.theme.spacing(2)};
  }

  &:hover,
  &.active {
    background: ${(props) => props.theme.palette.secondary.main};
    border-left-color: ${(props) => props.theme.palette.secondary.dark};
    color: ${(props) => props.theme.palette.common.white};
    font-weight: bold;
    text-decoration: underline 2px;
    text-underline-offset: 4px;

    svg {
      fill: ${(props) => props.theme.palette.common.white};
    }
  }
`;

export const MainLayoutNavBar = ({
  hasRestrictedAccess,
  hasAccessToGeneralAssembly,
  isTrusteeCouncilMember,
  accountQuality,
  closeSidebar,
  ...boxProps
}: MainLayoutNavBarProps) => {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const marketplaceLink = useMarketplaceLink();
  const isNotificationsEnabled = useHasFeatureFlagActivated(
    FeatureFlagNamesEnum.CustomerPortalUseServiceNotificationPushInfo,
  );

  const isCoOwner = account.quality === AccountQualityEnum.CoOwner;
  const hasLandlordFundsCallsPayment = useHasFeatureFlagActivated(
    FeatureFlagNamesEnum.CustomerPortalLandlordFundsCallsPayment,
  );

  const hasOffersAndServicesPage = isCoOwner || !!marketplaceLink;

  return (
    <Box {...boxProps} component="nav" aria-label={ariaLabel.leftNavBar}>
      <StyledItem
        onClick={closeSidebar}
        to={hasRestrictedAccess ? RESTRICTED_DASHBOARD_PAGE : DASHBOARD_PAGE}
      >
        <DashboardIcon />
        {ariaLabel.dashboardTitle}
      </StyledItem>
      <StyledItem
        onClick={() => {
          closeSidebar();
          logCustomEvent(AnalyticsCustomEventNameEnum.SidebarTicketsPage);
        }}
        to={TICKETS_PAGE}
      >
        <MessageIcon />
        {ariaLabel.ticketsTitle}
      </StyledItem>
      {isNotificationsEnabled && (
        <StyledItem onClick={closeSidebar} to={NOTIFICATIONS_PAGE}>
          <NotificationsIcon />
          {ariaLabel.notificationsTitle}
          <UnreadCountBadge />
        </StyledItem>
      )}
      <StyledItem onClick={closeSidebar} to={MISSION_INCIDENTS_PAGE}>
        <BuildIcon />
        {ariaLabel.missionIncidentsTitle}
      </StyledItem>
      {!hasRestrictedAccess && (
        <StyledItem onClick={closeSidebar} to={ACCOUNTING_PAGE}>
          <AccountBalanceWalletIcon />
          {ariaLabel.accountingTitle}
        </StyledItem>
      )}
      {accountQuality !== AccountQualityEnum.Landlord && (
        <StyledItem onClick={closeSidebar} to={PAYMENTS_PAGE}>
          <CreditCardIcon />
          {ariaLabel.paymentsTitle}
        </StyledItem>
      )}
      {accountQuality === AccountQualityEnum.Landlord &&
        !hasRestrictedAccess &&
        hasLandlordFundsCallsPayment && (
          <StyledItem
            onClick={closeSidebar}
            to={PATH_LANDLORD_REPAIR_FUNDS_CALLS}
          >
            <CreditCardIcon />
            {t("Mes paiements")}
          </StyledItem>
        )}
      {!hasRestrictedAccess && hasOffersAndServicesPage && (
        <StyledItem onClick={closeSidebar} to={OFFERS_AND_SERVICES_PAGE}>
          <RoomServiceIcon />
          {ariaLabel.offersAndServicesTitle}
        </StyledItem>
      )}
      {!hasRestrictedAccess && isTrusteeCouncilMember && (
        <StyledItem onClick={closeSidebar} to={COUNCIL_PAGE}>
          <GroupsIcon />
          {ariaLabel.councilTitle}
        </StyledItem>
      )}
      {!hasRestrictedAccess &&
        accountQuality === AccountQualityEnum.Landlord && (
          <StyledItem onClick={closeSidebar} to={TENANTS_BALANCE_PAGE}>
            <MapsHomeWorkIcon />
            {ariaLabel.tenantsBalanceTitle}
          </StyledItem>
        )}
      {!hasRestrictedAccess && (
        <StyledItem onClick={closeSidebar} to={ESTATE_PAGE}>
          <KeySharpIcon />
          {ariaLabel.estateTitle}
        </StyledItem>
      )}
      <StyledItem onClick={closeSidebar} to={DOCUMENTS_PAGE}>
        <DescriptionIcon />
        {ariaLabel.documentsTitle}
      </StyledItem>
      {!hasRestrictedAccess && hasAccessToGeneralAssembly && (
        <StyledItem onClick={closeSidebar} to={GENERAL_ASSEMBLY_PAGE}>
          <HowToVoteIcon />
          {ariaLabel.generalAssemblyTitle}
        </StyledItem>
      )}
    </Box>
  );
};
